import type { ApiResponse } from '@creator/sdk/creator-sdk';
import { displayNameToUsername, isPasswordValid, isUsernameValid } from '@creator/sdk/modules/account/account.helpers';
import type { User } from '@creator/sdk/modules/account/account.model';
import { post } from '@creator/sdk/network/api-service';
import { getContracetName, getDomainName } from '@src/config';
import creatorSdk from '@src/services/creator-sdk';
import { getIdToken, signInWithCustomToken } from 'firebase/auth';
import { Timestamp } from 'firebase/firestore/lite';
import { getTableRow, sign } from '../blockchain/eos-utils';
import { saveAuthToLocalStorage } from '../user/actions';
import { getMyUser, getUserIdByUsername } from '../user/helpers';
import { generateKeyPair, verifyUsernameAndPasswordApiCall } from './blockchain-auth';

export interface CreateAccountApiCallResult {
    keyPair?: KeyPair;
    user: User;
}

export interface ChangePasswordApiCallPayload {
    userId: string;
    firebaseUID: string;
    pubKey: string;
}

export interface UserBlockchainTableRow {
    id: string;
    social_id: string;
    user_name: string;
    domain: string;
    primary_public_key: string;
    secondary_public_key: string;
    nonce: number;
}

export async function getBlockchainUser(userId: string) {
    return getBlockchainUserById(userId);
}

export async function getBlockchainUserById(id: string): Promise<UserBlockchainTableRow> {
    const result = await getTableRow({
        contract: getContracetName(),
        scope: getContracetName(),
        table: 'user',
        key: id,
        keyField: 'id',
    });

    if (!result || !result.row) return null;

    return result.row;
}

export async function createAccountApiCall(
    displayName: string,
    password?: string,
    referredBy?: string,
    referredTokenName?: string,
    refBy?: string,
    refFrom?: string
): Promise<CreateAccountApiCallResult> {
    const username = displayNameToUsername(displayName);
    if (!isUsernameValid(username)) throw new Error('invalidUsername');

    const userId = getUserIdByUsername(username, getDomainName());
    // Password is not mandatory (ie social login), if password was given, we can create a key pair.
    const keyPair = password ? generateKeyPair(userId, password) : undefined;
    const firebaseUser = getCurrentFirebaseUser();
    if (!firebaseUser) throw new Error('auth required - inavlid firebase user');
    const token = await getFirebaseIdToken(true);

    const publicKey = keyPair?.publicKey; // undefined is ok for social login (Backend treat empty strings as valid inputs)

    const res = await post<ApiResponse<any>>(
        '/user/signup',
        {
            username,
            displayName,
            referredBy,
            referredTokenName,
            sharedBy: refBy,
            referredFrom: refFrom,
            domain: getDomainName(),
            firebaseUID: firebaseUser.uid,
            primaryPublicKey: publicKey,
            secondaryPublicKey: publicKey,
        },
        { Authorization: `Bearer ${token}`, 'X-Firebase-AppCheck': '' },
        false
    );

    await getFirebaseIdToken(true); // force genrating a new token for new user https://trello.com/c/Cw6AEap3/3766-new-user-after-call-to-create-user-need-to-refresh-access-token

    const payload = res.data.payload;
    const { user } = payload;

    return {
        keyPair,
        user: { id: user.blockchainId, ...user },
    };
}

export async function changeAccountPasswordApiCall(userId: string, newPassword: string) {
    if (!isPasswordValid(newPassword)) throw new Error('invalidPassword');

    const user = await getMyUser();
    const firebaseUser = getCurrentFirebaseUser();
    if (!user || !firebaseUser) throw new Error('invalidUser');

    const { firebaseUID } = user;

    const keyPair = generateKeyPair(userId, newPassword);
    const payload: ChangePasswordApiCallPayload = {
        firebaseUID,
        pubKey: keyPair.publicKey,
        userId,
    };

    const token = await getFirebaseIdToken();
    await post<ApiResponse<any>>('/changePassword', { ...payload }, { Authorization: `Bearer ${token}`, 'X-Firebase-AppCheck': '' });

    return { user, firebaseUser, keyPair };
}

export function getCurrentFirebaseUser() {
    return creatorSdk.getAuth().currentUser;
}

export async function getFirebaseIdToken(forceRefresh?: boolean) {
    const firebaseUser = getCurrentFirebaseUser();
    if (!firebaseUser) throw new Error('auth required - inavlid firebase user');
    return getIdToken(firebaseUser, forceRefresh);
}

export async function firebaseSignInApiCall(userId: string, keyPair: KeyPair) {
    const payload = {
        userId,
        time: Timestamp.now().toMillis(),
        publicKey: keyPair.publicKey,
    };

    const buff = payload.userId + payload.publicKey + payload.time;
    const sig = sign(buff, keyPair.privateKey);
    const res = await post<ApiResponse<any>>('/customAuthentication', { payload, sig }, { 'X-Firebase-AppCheck': '' });

    const { payload: authToken } = res.data;

    return signInWithCustomToken(creatorSdk.getAuth(), authToken.token);
}

export async function verifyUsernameAndPassword(username: string, password: string, domainName: string): Promise<User> {
    const { keyPair, user } = await verifyUsernameAndPasswordApiCall(username, password, domainName);

    const firebaseUser = getCurrentFirebaseUser();
    if (!firebaseUser) throw new Error('verifyUsernameAndPassword invalid firebaseUser');

    const { privateKey, publicKey } = keyPair;

    saveAuthToLocalStorage(user.id, username, publicKey, privateKey);
    return user;
}
